import React, { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Trash2, 
  Upload, 
  ChevronDown, 
  Move, 
  Maximize2, 
  Type, 
  Palette,
  Settings,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  MousePointer2,
  RotateCw,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignHorizontalJustifyCenter,
} from 'lucide-react';
import { 
  LabelElement, 
  TextElement, 
  BarcodeElement,
  ShapeElement,
  ImageElement,
  IconElement,
  BARCODE_TYPE_OPTIONS,
  BARCODE_VALIDATION,
  BARCODE_SAMPLE_CONTENT,
} from '@/types/label';
import { validateBarcodeContent } from '@/lib/barcodes';
import { IconPicker } from './IconPicker';

interface ElementPropertiesProps {
  element: LabelElement | null;
  onUpdateElement: (id: string, updates: Partial<LabelElement>) => void;
  onDeleteElement: (id: string) => void;
  labelWidth?: number;
  labelHeight?: number;
}

interface PropertySectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  sectionKey: string; // Unique key for localStorage persistence
  defaultOpen?: boolean;
}

const SECTION_STATE_KEY = 'label-forge-property-sections';

const getSectionStates = (): Record<string, boolean> => {
  try {
    const stored = localStorage.getItem(SECTION_STATE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveSectionState = (key: string, isOpen: boolean) => {
  try {
    const states = getSectionStates();
    states[key] = isOpen;
    localStorage.setItem(SECTION_STATE_KEY, JSON.stringify(states));
  } catch {
    // Ignore localStorage errors
  }
};

const PropertySection: React.FC<PropertySectionProps> = ({ 
  title, 
  icon, 
  children, 
  sectionKey,
  defaultOpen = true 
}) => {
  const [isOpen, setIsOpen] = React.useState(() => {
    const states = getSectionStates();
    return states[sectionKey] !== undefined ? states[sectionKey] : defaultOpen;
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    saveSectionState(sectionKey, open);
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-1 hover:bg-muted/50 rounded-md transition-colors group">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          <span className="text-xs font-medium">{title}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 pb-3 px-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const ElementProperties: React.FC<ElementPropertiesProps> = ({
  element,
  onUpdateElement,
  onDeleteElement,
  labelWidth = 50.8,
  labelHeight = 25.4,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!element) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
          <MousePointer2 className="w-5 h-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">No element selected</p>
        <p className="text-xs text-muted-foreground">Click on an element in the canvas to edit its properties</p>
      </div>
    );
  }

  const updatePosition = (axis: 'x' | 'y', value: string) => {
    const num = parseFloat(value) || 0;
    onUpdateElement(element.id, {
      position: { ...element.position, [axis]: num },
    });
  };

  const updateSize = (dimension: 'width' | 'height', value: string) => {
    const num = parseFloat(value) || 1;
    onUpdateElement(element.id, {
      size: { ...element.size, [dimension]: num },
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        onUpdateElement(element.id, { src: base64 } as Partial<ImageElement>);
      };
      reader.readAsDataURL(file);
    }
  };

  const elementTypeLabels: Record<string, string> = {
    text: 'Text',
    qrcode: 'QR Code',
    barcode: 'Barcode',
    image: 'Image',
    shape: 'Shape',
    icon: 'Icon',
  };

  return (
    <div className="p-3 space-y-1">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-border/50">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
            <Type className="w-3.5 h-3.5 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">{elementTypeLabels[element.type] || element.type}</h3>
            <p className="text-[10px] text-muted-foreground font-mono">{element.id.slice(0, 12)}...</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onDeleteElement(element.id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Quick toggles */}
      <div className="flex items-center gap-2 py-3 border-b border-border/50">
        <Button
          variant={element.locked ? "secondary" : "ghost"}
          size="sm"
          className="flex-1 h-8 gap-1.5 text-xs"
          onClick={() => onUpdateElement(element.id, { locked: !element.locked })}
        >
          {element.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          {element.locked ? 'Locked' : 'Unlocked'}
        </Button>
        <Button
          variant={element.visible === false ? "secondary" : "ghost"}
          size="sm"
          className="flex-1 h-8 gap-1.5 text-xs"
          onClick={() => onUpdateElement(element.id, { visible: element.visible === false })}
        >
          {element.visible !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {element.visible !== false ? 'Visible' : 'Hidden'}
        </Button>
      </div>

      {/* Rotation */}
      <PropertySection title="Rotation" icon={<RotateCw className="w-3.5 h-3.5" />} sectionKey="rotation">
        <div className="space-y-2">
          <div className="flex gap-1">
            {[0, 90, 180, 270].map((deg) => (
              <Button
                key={deg}
                variant={(element.rotation || 0) === deg ? "secondary" : "ghost"}
                size="sm"
                className="flex-1 h-8 text-xs"
                onClick={() => onUpdateElement(element.id, { rotation: deg })}
              >
                {deg}°
              </Button>
            ))}
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Custom (°)</Label>
            <Input
              type="number"
              step="1"
              min="0"
              max="359"
              value={element.rotation || 0}
              onChange={(e) => onUpdateElement(element.id, { rotation: (parseFloat(e.target.value) || 0) % 360 })}
              className="h-8 text-xs"
            />
          </div>
        </div>
      </PropertySection>

      {/* Align to label */}
      <div className="flex items-center gap-1 py-2 border-b border-border/50">
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 h-8 gap-1 text-xs"
          title="Center horizontally"
          onClick={() => {
            const newX = (labelWidth - element.size.width) / 2;
            onUpdateElement(element.id, { position: { ...element.position, x: parseFloat(newX.toFixed(2)) } });
          }}
        >
          <AlignCenterHorizontal className="w-3.5 h-3.5" />
          H-Center
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 h-8 gap-1 text-xs"
          title="Center vertically"
          onClick={() => {
            const newY = (labelHeight - element.size.height) / 2;
            onUpdateElement(element.id, { position: { ...element.position, y: parseFloat(newY.toFixed(2)) } });
          }}
        >
          <AlignCenterVertical className="w-3.5 h-3.5" />
          V-Center
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 h-8 gap-1 text-xs"
          title="Center both"
          onClick={() => {
            const newX = (labelWidth - element.size.width) / 2;
            const newY = (labelHeight - element.size.height) / 2;
            onUpdateElement(element.id, { position: { x: parseFloat(newX.toFixed(2)), y: parseFloat(newY.toFixed(2)) } });
          }}
        >
          <AlignHorizontalJustifyCenter className="w-3.5 h-3.5" />
          Center
        </Button>
      </div>

      {/* Position Section */}
      <PropertySection title="Position" icon={<Move className="w-3.5 h-3.5" />} sectionKey="position">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">X (mm)</Label>
            <Input
              type="number"
              step="0.5"
              value={element.position.x}
              onChange={(e) => updatePosition('x', e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Y (mm)</Label>
            <Input
              type="number"
              step="0.5"
              value={element.position.y}
              onChange={(e) => updatePosition('y', e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>
      </PropertySection>

      {/* Size Section */}
      <PropertySection title="Size" icon={<Maximize2 className="w-3.5 h-3.5" />} sectionKey="size">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Width (mm)</Label>
            <Input
              type="number"
              step="0.5"
              min="1"
              value={element.size.width}
              onChange={(e) => updateSize('width', e.target.value)}
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Height (mm)</Label>
            <Input
              type="number"
              step="0.5"
              min="1"
              value={element.size.height}
              onChange={(e) => updateSize('height', e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>
      </PropertySection>

      {/* Content Section - only for elements that have content */}
      {(element.type === 'text' || element.type === 'qrcode' || element.type === 'barcode') && (
        <PropertySection title="Content" icon={<Type className="w-3.5 h-3.5" />} sectionKey="content">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Text Content</Label>
              <Input
                value={element.content || ''}
                onChange={(e) => onUpdateElement(element.id, { content: e.target.value })}
                className="h-8 text-xs"
                placeholder="Enter text or {{fieldName}}"
              />
              <p className="text-[10px] text-muted-foreground">Use {"{{field}}"} for dynamic values</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Field Name</Label>
              <Input
                value={element.field || ''}
                onChange={(e) => onUpdateElement(element.id, { field: e.target.value || undefined })}
                placeholder="e.g., productName"
                className="h-8 text-xs"
              />
            </div>
          </div>
        </PropertySection>
      )}

      {/* Text-specific properties */}
      {element.type === 'text' && (
        <PropertySection title="Typography" icon={<Type className="w-3.5 h-3.5" />} sectionKey="typography">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Font Size</Label>
                <Input
                  type="number"
                  min="4"
                  max="72"
                  value={(element as TextElement).fontSize}
                  onChange={(e) => 
                    onUpdateElement(element.id, { fontSize: parseInt(e.target.value) || 10 })
                  }
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Weight</Label>
                <Select
                  value={(element as TextElement).fontWeight}
                  onValueChange={(value: 'normal' | 'bold') => 
                    onUpdateElement(element.id, { fontWeight: value })
                  }
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="bold">Bold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Font Family</Label>
              <Select
                value={(element as TextElement).fontFamily}
                onValueChange={(value) => onUpdateElement(element.id, { fontFamily: value })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Arial">Arial</SelectItem>
                  <SelectItem value="Helvetica">Helvetica</SelectItem>
                  <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                  <SelectItem value="Courier New">Courier New</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Alignment</Label>
              <div className="flex gap-1">
                {(['left', 'center', 'right'] as const).map((align) => (
                  <Button
                    key={align}
                    variant={(element as TextElement).textAlign === align ? "secondary" : "ghost"}
                    size="sm"
                    className="flex-1 h-8 text-xs capitalize"
                    onClick={() => onUpdateElement(element.id, { textAlign: align })}
                  >
                    {align}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Text Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={(element as TextElement).color || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { color: e.target.value })}
                  className="h-8 w-12 p-1 cursor-pointer"
                />
                <Input
                  value={(element as TextElement).color || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { color: e.target.value })}
                  className="h-8 flex-1 text-xs font-mono"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>
        </PropertySection>
      )}

      {/* Barcode-specific properties */}
      {element.type === 'barcode' && (
        <PropertySection title="Barcode Settings" icon={<Settings className="w-3.5 h-3.5" />} sectionKey="barcode-settings">
          <div className="space-y-3">
            {/* Barcode Type - grouped by category */}
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Barcode Type</Label>
              <Select
                value={(element as BarcodeElement).barcodeType || 'code128'}
                onValueChange={(value) => {
                  const currentContent = (element as BarcodeElement).content || '';
                  const newValidation = validateBarcodeContent(currentContent, value);
                  const updates: Partial<BarcodeElement> = { barcodeType: value as any };
                  // Auto-set sample content if current content is invalid for the new type
                  if (newValidation && BARCODE_SAMPLE_CONTENT[value]) {
                    updates.content = BARCODE_SAMPLE_CONTENT[value];
                  }
                  onUpdateElement(element.id, updates);
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['General', 'Retail', 'Logistics', '2D', 'Specialty'] as const).map((cat) => {
                    const opts = BARCODE_TYPE_OPTIONS.filter((o) => o.category === cat);
                    if (opts.length === 0) return null;
                    return (
                      <React.Fragment key={cat}>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">{cat}</div>
                        {opts.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex flex-col">
                              <span>{opt.label}</span>
                              <span className="text-[10px] text-muted-foreground">{opt.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Validation warning */}
            {(() => {
              const barcodeEl = element as BarcodeElement;
              const resolvedContent = barcodeEl.content || '';
              const validationError = validateBarcodeContent(resolvedContent, barcodeEl.barcodeType || 'code128');
              const rules = BARCODE_VALIDATION[barcodeEl.barcodeType || 'code128'];
              if (validationError) {
                return (
                  <div className="p-2 rounded-md bg-destructive/10 border border-destructive/20">
                    <p className="text-[10px] text-destructive font-medium">⚠ {validationError}</p>
                    {rules && <p className="text-[10px] text-muted-foreground mt-0.5">Format: {rules.message}</p>}
                  </div>
                );
              }
              return null;
            })()}

            {/* Show Text */}
            <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
              <Label className="text-xs">Show Text Below</Label>
              <Switch
                checked={(element as BarcodeElement).includeText}
                onCheckedChange={(checked) => 
                  onUpdateElement(element.id, { includeText: checked })
                }
              />
            </div>

            {/* Text Size (only when includeText is on) */}
            {(element as BarcodeElement).includeText && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Text Size</Label>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted">
                    {(element as BarcodeElement).textSize || 8}pt
                  </span>
                </div>
                <Input
                  type="range"
                  min="4"
                  max="20"
                  step="1"
                  value={(element as BarcodeElement).textSize || 8}
                  onChange={(e) => 
                    onUpdateElement(element.id, { textSize: parseInt(e.target.value) })
                  }
                  className="h-2 cursor-pointer"
                />
              </div>
            )}

            {/* Bar Width */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Bar Width</Label>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted">
                  {(element as BarcodeElement).barScale || 2}x
                </span>
              </div>
              <Input
                type="range"
                min="0.5"
                max="8"
                step="0.5"
                value={(element as BarcodeElement).barScale || 2}
                onChange={(e) => 
                  onUpdateElement(element.id, { barScale: parseFloat(e.target.value) })
                }
                className="h-2 cursor-pointer"
              />
              <p className="text-[10px] text-muted-foreground">
                Wider bars improve scanner readability
              </p>
            </div>

            {/* Bar Height Ratio */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Bar Height</Label>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted">
                  {Math.round(((element as BarcodeElement).barHeightRatio || 0.8) * 100)}%
                </span>
              </div>
              <Input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={(element as BarcodeElement).barHeightRatio || 0.8}
                onChange={(e) => 
                  onUpdateElement(element.id, { barHeightRatio: parseFloat(e.target.value) })
                }
                className="h-2 cursor-pointer"
              />
              <p className="text-[10px] text-muted-foreground">
                Taller bars are easier to scan
              </p>
            </div>

            {/* Quiet Zone */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Quiet Zone</Label>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted">
                  {(element as BarcodeElement).quietZone ?? 2}
                </span>
              </div>
              <Input
                type="range"
                min="0"
                max="20"
                step="1"
                value={(element as BarcodeElement).quietZone ?? 2}
                onChange={(e) => 
                  onUpdateElement(element.id, { quietZone: parseInt(e.target.value) })
                }
                className="h-2 cursor-pointer"
              />
              <p className="text-[10px] text-muted-foreground">
                Margin around barcode for reliable scanning
              </p>
            </div>

            <Separator />

            {/* Foreground Color */}
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Bar Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={(element as BarcodeElement).foregroundColor || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { foregroundColor: e.target.value })}
                  className="h-8 w-12 p-1 cursor-pointer"
                />
                <Input
                  value={(element as BarcodeElement).foregroundColor || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { foregroundColor: e.target.value })}
                  className="h-8 flex-1 text-xs font-mono"
                  placeholder="#000000"
                />
              </div>
            </div>

            {/* Background Color */}
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Background Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={(element as BarcodeElement).backgroundColor || '#ffffff'}
                  onChange={(e) => onUpdateElement(element.id, { backgroundColor: e.target.value })}
                  className="h-8 w-12 p-1 cursor-pointer"
                />
                <Input
                  value={(element as BarcodeElement).backgroundColor || '#ffffff'}
                  onChange={(e) => onUpdateElement(element.id, { backgroundColor: e.target.value })}
                  className="h-8 flex-1 text-xs font-mono"
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>
        </PropertySection>
      )}

      {/* Image-specific properties */}
      {element.type === 'image' && (
        <PropertySection title="Image Settings" icon={<Settings className="w-3.5 h-3.5" />} sectionKey="image-settings">
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 h-9"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4" />
              Upload Image
            </Button>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Fit Mode</Label>
              <Select
                value={(element as ImageElement).objectFit || 'contain'}
                onValueChange={(value: 'contain' | 'cover' | 'fill') => 
                  onUpdateElement(element.id, { objectFit: value })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contain">Contain</SelectItem>
                  <SelectItem value="cover">Cover</SelectItem>
                  <SelectItem value="fill">Fill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Corner Radius</Label>
              <Input
                type="number"
                min="0"
                value={(element as ImageElement).borderRadius || 0}
                onChange={(e) => 
                  onUpdateElement(element.id, { borderRadius: parseInt(e.target.value) || 0 })
                }
                className="h-8 text-xs"
              />
            </div>
          </div>
        </PropertySection>
      )}

      {/* Shape-specific properties */}
      {element.type === 'shape' && (
        <PropertySection title="Shape Settings" icon={<Palette className="w-3.5 h-3.5" />} sectionKey="shape-settings">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Shape Type</Label>
              <Select
                value={(element as ShapeElement).shapeType}
                onValueChange={(value: 'rectangle' | 'circle' | 'line' | 'triangle') => 
                  onUpdateElement(element.id, { shapeType: value })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rectangle">Rectangle</SelectItem>
                  <SelectItem value="circle">Circle</SelectItem>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="triangle">Triangle</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Fill Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={(element as ShapeElement).fillColor || '#ffffff'}
                  onChange={(e) => onUpdateElement(element.id, { fillColor: e.target.value })}
                  className="h-8 w-12 p-1 cursor-pointer"
                />
                <Input
                  value={(element as ShapeElement).fillColor || '#ffffff'}
                  onChange={(e) => onUpdateElement(element.id, { fillColor: e.target.value })}
                  className="h-8 flex-1 text-xs font-mono"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => onUpdateElement(element.id, { fillColor: 'transparent' })}
                >
                  None
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Stroke Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={(element as ShapeElement).strokeColor || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { strokeColor: e.target.value })}
                  className="h-8 w-12 p-1 cursor-pointer"
                />
                <Input
                  value={(element as ShapeElement).strokeColor || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { strokeColor: e.target.value })}
                  className="h-8 flex-1 text-xs font-mono"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Stroke Width</Label>
              <Input
                type="number"
                min="0"
                max="10"
                step="0.5"
                value={(element as ShapeElement).strokeWidth || 1}
                onChange={(e) => 
                  onUpdateElement(element.id, { strokeWidth: parseFloat(e.target.value) || 1 })
                }
                className="h-8 text-xs"
              />
            </div>

            {(element as ShapeElement).shapeType === 'rectangle' && (
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Corner Radius (mm)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={(element as ShapeElement).borderRadius || 0}
                  onChange={(e) => 
                    onUpdateElement(element.id, { borderRadius: parseFloat(e.target.value) || 0 })
                  }
                  className="h-8 text-xs"
                />
              </div>
            )}
          </div>
        </PropertySection>
      )}

      {/* Icon-specific properties */}
      {element.type === 'icon' && (
        <PropertySection title="Icon Settings" icon={<Palette className="w-3.5 h-3.5" />} sectionKey="icon-settings">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Icon</Label>
              <IconPicker
                value={(element as IconElement).iconName}
                onSelect={(iconName) => onUpdateElement(element.id, { iconName })}
                trigger={
                  <Button variant="outline" size="sm" className="w-full justify-start gap-2 h-8 text-xs">
                    {(element as IconElement).iconName || 'Select icon'}
                  </Button>
                }
              />
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={(element as IconElement).color || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { color: e.target.value })}
                  className="h-8 w-12 p-1 cursor-pointer"
                />
                <Input
                  value={(element as IconElement).color || '#000000'}
                  onChange={(e) => onUpdateElement(element.id, { color: e.target.value })}
                  className="h-8 flex-1 text-xs font-mono"
                />
              </div>
            </div>
          </div>
        </PropertySection>
      )}
    </div>
  );
};
